import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconBuildingStore,
  IconCheck,
  IconChevronDown,
} from '@tabler/icons-react';
import { useEstabelecimentosStore } from '../store/estabelecimentos.store';
import { useAcessoStore, temAtribuicaoRbac } from '../store/acesso.store';
import { useEstabPreferencial } from '../lib/use-estab-preferencial';
import { LARGURA_MAX_ROTULO_SELETOR } from './seletor-largura';
import { cn } from './ui/cn';

/**
 * [AB#178 / ADR-030 / D10 #915] Seletor reusável de Estabelecimento.
 *
 * Comportamento:
 *  - **Single-unit transparente**: quando o tenant tem 1 estabelecimento
 *    ativo, o componente NÃO renderiza nada e dispara `onChange(id)` uma
 *    única vez com o id do único estab. A tela cuida do estado como se
 *    nada existisse.
 *  - **Multi-unit**: renderiza botão-dropdown com largura ao conteúdo
 *    (`w-auto`), ícone `IconBuildingStore` à esquerda, chevron à direita.
 *    Abre menu listando "Todos" + cada estab; troca por click.
 *  - **[fix refresh-sistêmico]** Não fetcha nada. Consome cache global.
 *  - **[D10 #915] Duas fontes, escolhidas por `temAtribuicaoRbac`:** usuário
 *    COM pelo menos uma `UsuarioAcesso` lê `acesso.estabelecimentos_permitidos`
 *    do `/auth/me` (`useAcessoStore`, populado pelo `AcessoBoot`) — não depende
 *    mais de `GET /estabelecimentos` (só-ADMIN no legado; um Atendente levava
 *    403 silencioso ali, o motivo original do D10). Usuário SEM nenhuma
 *    atribuição (ainda sem backfill) cai no caminho de sempre:
 *    `useEstabelecimentosStore` (populado pelo `EstabelecimentosBoot`,
 *    `GET /estabelecimentos`) — preserva o comportamento atual até o backfill
 *    rodar pra esse tenant. Em ambas, "Todos" (`X-Estabelecimento-Id` ausente,
 *    ADR §13.3) significa "todos os permitidos" — tenant-wide ou não —, então
 *    a visibilidade da opção continua controlada só pela prop `incluirTodos`.
 *  - Acessível: `role="combobox"` no trigger, `role="listbox"` no menu,
 *    `aria-expanded`, `aria-controls`. Fecha por click-outside + Esc.
 *  - **Estabelecimento preferencial (AB#295)**: em multi-unit, aplica
 *    `estabelecimento_preferencial_id` do usuário (`useEstabPreferencial`) como DEFAULT
 *    inicial — emite `onChange(preferencial)` uma vez no mount quando `value=null`. A
 *    PÁGINA não precisa chamar `useEstabPreferencial()`: só `useState<number|null>(null)`
 *    + passar `value`/`onChange`. Aplicado uma vez só (depois o usuário troca livremente
 *    pra "Todos" sem ser re-forçado); só aplica se a unidade preferida existir e estiver
 *    ativa. Opt-out via prop `aplicarPreferencial={false}`.
 *
 * Padrão DS:
 *  - Altura `h-9` (mesma das pills de período / botões de filtro)
 *  - Border + shadow consistentes com `Button variant="secondary"`
 *  - Ícone teal `text-primary` pra remeter ao tipo de filtro
 *
 * NÃO renderiza `<label>` externo — o ícone + texto comunicam o filtro.
 * Quem precisar label semântico fora pode envolver, mas o padrão é
 * limpo (sem label).
 */

interface SeletorEstabelecimentoProps {
  /** id selecionado; null = "Todos". */
  value: number | null;
  /** Disparado quando o usuário troca. Single-unit dispara 1x no mount com o id único. */
  onChange: (id: number | null) => void;
  /** Quando false, não oferece a opção "Todos" — força escolher uma unidade. Default true. */
  incluirTodos?: boolean;
  /**
   * [AB#295] Quando true (default), em multi-unit aplica o estabelecimento
   * PREFERENCIAL do usuário (`estabelecimento_preferencial_id`) como valor
   * inicial — emite `onChange(preferencial)` uma única vez no mount se
   * `value=null`. Centraliza o comportamento no componente (ADR-030): toda
   * tela que usa o seletor herda o default sem precisar lembrar de aplicar.
   * Passe `false` para abrir sempre em "Todos".
   */
  aplicarPreferencial?: boolean;
  /** Texto do trigger quando `value=null` e `incluirTodos=true`. Default "Todos". */
  rotuloTodos?: string;
  /** Classes extras no trigger (margens, etc.). */
  className?: string;
  /**
   * [item 1/2, PR !901] Opt-in, aditivo — default `undefined` (nenhum outro chamador muda de
   * comportamento). Quando presente, restringe a lista a só esses ids — ex.: Mapeamento
   * contábil, onde só fazem sentido os estabelecimentos do LIVRO selecionado
   * (`Estabelecimento.livro_id`). Participa normalmente da transparência single-unit e do
   * preferencial (ambos leem a lista já restrita).
   */
  idsPermitidos?: number[];
  /**
   * [fix] Alinhamento do menu em relação ao trigger. Default "right"
   * funciona pra headers (PageHead.actions). Em containers estreitos
   * (ex.: sidebar do inbox) o `right-0` faz o menu vazar pra esquerda
   * fora do container — usar "left" pra ancorar à esquerda do trigger.
   */
  align?: 'left' | 'right';
}

export function SeletorEstabelecimento({
  value,
  onChange,
  incluirTodos = true,
  aplicarPreferencial = true,
  rotuloTodos = 'Todos',
  className,
  align = 'right',
  idsPermitidos,
}: SeletorEstabelecimentoProps): JSX.Element | null {
  // [D10 #915] Fonte nova: /auth/me → acesso.estabelecimentos_permitidos,
  // só usada quando o usuário já tem pelo menos uma UsuarioAcesso. Fonte
  // legada: GET /estabelecimentos (cache do EstabelecimentosBoot),
  // filtrada aos ATIVOS — admin pode ter desativado estab via
  // /settings/estabelecimentos e a UI de filtro nunca deve oferecer um inativo.
  const acesso = useAcessoStore((s) => s.acesso);
  const acessoCarregado = useAcessoStore((s) => s.carregado);
  const usaFonteNova = temAtribuicaoRbac(acesso);

  const todosLegado = useEstabelecimentosStore((s) => s.estabelecimentos);
  const carregadoLegado = useEstabelecimentosStore((s) => s.carregado);
  const estabsLegado = useMemo(() => todosLegado.filter((e) => e.ativo), [todosLegado]);

  const carregado = usaFonteNova ? acessoCarregado : carregadoLegado;
  const estabsBase = useMemo(
    () => (usaFonteNova ? (acesso?.estabelecimentos_permitidos ?? []) : estabsLegado),
    [usaFonteNova, acesso, estabsLegado],
  );
  const estabs = useMemo(
    () => (idsPermitidos ? estabsBase.filter((e) => idsPermitidos.includes(e.id)) : estabsBase),
    [estabsBase, idsPermitidos],
  );
  // "Todos" (X-Estabelecimento-Id ausente, ADR §13.3) sempre significa "todos os
  // permitidos" — tenant-wide ou não. Só nunca aparece quando o single-unit
  // transparente abaixo já escondeu o componente inteiro (nesse caso não há
  // "Todos" vs. "a unidade" pra escolher: só existe 1 caminho possível).

  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Ref estável pro callback do pai — evita re-disparo do effect de
  // single-unit por causa de referência nova de onChange a cada render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // [AB#295 / ADR-030] Estabelecimento preferencial do usuário — default dos
  // filtros. Aplicado UMA vez no mount (ref de controle) pra não brigar com a
  // escolha manual de "Todos" depois.
  const preferencial = useEstabPreferencial();
  const preferencialAplicadoRef = useRef(false);

  // [fix refresh-sistêmico] Single-unit transparente: quando o cache
  // resolve com 1 estab ativo, notifica o pai com o id único. Disparo
  // só acontece se `value !== id` (idempotente) — evita cascata
  // redundante que reseta o debounce do ListaConversas.useEffect#145.
  useEffect(() => {
    if (!carregado) return;
    if (estabs.length !== 1) return;
    const idUnico = estabs[0]!.id;
    if (value === idUnico) return; // já está nesse estab — no-op
    onChangeRef.current(idUnico);
  }, [carregado, estabs, value]);

  // [AB#295] Multi-unit: aplica o estabelecimento preferencial como default
  // inicial (uma vez). Espelha o auto-emit do single-unit acima. Guarda:
  //  - só se `aplicarPreferencial` (opt-out por tela);
  //  - só uma vez (preferencialAplicadoRef) — depois o usuário troca livre;
  //  - só se `value=null` (não sobrescreve valor já definido pelo pai);
  //  - só se a preferida existir e estiver ATIVA na lista.
  useEffect(() => {
    if (!aplicarPreferencial || preferencialAplicadoRef.current) return;
    if (!carregado || estabs.length <= 1) return;
    preferencialAplicadoRef.current = true;
    if (value !== null) return; // pai já definiu uma unidade
    if (preferencial == null) return; // sem preferência → fica "Todos"
    if (!estabs.some((e) => e.id === preferencial)) return; // preferida inativa/sumiu
    onChangeRef.current(preferencial);
  }, [aplicarPreferencial, carregado, estabs, value, preferencial]);

  // [D10 #915] Revalida a unidade ativa sempre que a lista de permitidas
  // mudar — cobre o caso de conceder/revogar acesso ao PRÓPRIO usuário
  // logado (DrawerAcessosUsuario força um recarregar() do useAcessoStore
  // depois de uma ação assim). Se a unidade selecionada deixou de estar em
  // `estabelecimentos_permitidos`, cai pra primeira permitida (ou "Todos",
  // sem nenhuma sobrando).
  useEffect(() => {
    if (!carregado || value === null) return;
    if (estabs.some((e) => e.id === value)) return;
    onChangeRef.current(estabs[0]?.id ?? null);
  }, [carregado, estabs, value]);

  // Click-outside fecha o menu.
  useEffect(() => {
    if (!aberto) return;
    const onClick = (ev: MouseEvent): void => {
      if (!containerRef.current?.contains(ev.target as Node)) {
        setAberto(false);
      }
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [aberto]);

  const selecionado = useMemo(
    () => (value !== null ? estabs.find((e) => e.id === value) : undefined),
    [estabs, value],
  );

  // [fix refresh-sistêmico] Não renderiza NADA até o cache resolver —
  // evita flash do trigger no caso single-unit (1 estab) onde o
  // componente acabaria escondendo o trigger logo em seguida.
  if (!carregado) return null;
  // Single-unit transparente: caller já foi notificado via onChange,
  // segue funcionando sem dropdown.
  if (estabs.length <= 1) return null;

  const rotulo = selecionado ? selecionado.nome : rotuloTodos;

  function escolher(id: number | null): void {
    setAberto(false);
    onChange(id);
    triggerRef.current?.focus();
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-label={`Filtrar por estabelecimento. Atual: ${rotulo}`}
        title="Filtrar por estabelecimento"
        className={cn(
          'inline-flex h-9 w-auto items-center gap-2 rounded-md border border-border-v2 bg-bg-surface-v2 px-3 text-sm text-text-primary',
          'hover:bg-bg-canvas-v2 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-[var(--brand-300)]',
          aberto && 'ring-2 ring-[var(--brand-300)]',
        )}
      >
        <IconBuildingStore size={16} className="text-primary shrink-0" />
        <span className={cn('truncate', LARGURA_MAX_ROTULO_SELETOR)}>{rotulo}</span>
        <IconChevronDown
          size={14}
          className={cn(
            'text-text-tertiary shrink-0 transition-transform',
            aberto && 'rotate-180',
          )}
        />
      </button>

      {aberto && (
        <div
          role="listbox"
          aria-label="Estabelecimentos"
          className={cn(
            'absolute top-[calc(100%+6px)] z-50 min-w-[220px] rounded-md border border-border-v2 bg-bg-elevated p-1.5 shadow-lg animate-[tcl-toast-in_0.16s_var(--ease)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {incluirTodos && (
            <OpcaoMenu
              ativo={value === null}
              onClick={() => escolher(null)}
            >
              {rotuloTodos}
            </OpcaoMenu>
          )}
          {estabs.map((e) => (
            <OpcaoMenu
              key={e.id}
              ativo={value === e.id}
              onClick={() => escolher(e.id)}
            >
              {e.nome}
            </OpcaoMenu>
          ))}
        </div>
      )}
    </div>
  );
}

function OpcaoMenu({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="option"
      aria-selected={ativo}
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-sm px-3 py-1.5 text-left text-sm text-text-primary',
        'hover:bg-bg-canvas-v2',
        ativo && 'bg-primary-soft text-primary',
      )}
    >
      <span className="truncate">{children}</span>
      {ativo && <IconCheck size={14} className="text-primary shrink-0" />}
    </button>
  );
}
